import { postChangePassword } from 'api/auth'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Ui from 'ui'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import { error, initial, isError } from 'utils/remoteData'
import {
  newPasswordSchema,
  oldPasswordSchema,
  schemasValidate,
} from 'utils/schemas'

import Spinner from '../Spinner'

const initialFormError = {
  password: false,
  newPassword: false,
}

const initialFormErrorLabel = {
  password: undefined,
  newPassword: undefined,
}

function ModalChangePassword({ onClose, onPasswordChanged }) {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')

  const [hasFormError, setHasFormError] = useState(false)
  const [formError, setFormError] = useState(initialFormError)
  const [formErrorLabel, setFormErrorLabel] = useState(initialFormErrorLabel)
  const [isSubmitting, setSubmitting] = useState(false)
  const [submitRequest, setSubmitRequest] = useState(initial())

  const validateOldPassword = useCallback(
    () =>
      schemasValidate(
        [['oldPassword', oldPassword, oldPasswordSchema]],
        setFormError,
        setFormErrorLabel
      ),
    [oldPassword]
  )

  const validateNewPassword = useCallback(
    () =>
      schemasValidate(
        [['newPassword', newPassword, newPasswordSchema]],
        setFormError,
        setFormErrorLabel
      ),
    [newPassword]
  )

  useEffect(() => {
    formError?.password === true && validateOldPassword()
    formError?.newPassword === true && validateNewPassword()
  }, [
    formError.newPassword,
    formError.password,
    validateNewPassword,
    validateOldPassword,
  ])

  useEffect(() => {
    setHasFormError(Object.values(formError).some((error) => error === true))
  }, [formError])

  const validateForm = useCallback(
    () =>
      schemasValidate(
        [
          ['oldPassword', oldPassword, oldPasswordSchema],
          ['newPassword', newPassword, newPasswordSchema],
        ],
        setFormError,
        setFormErrorLabel
      ),
    [newPassword, oldPassword]
  )

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)
    try {
      if (validateForm()) {
        await postChangePassword({ oldPassword, newPassword })
        onPasswordChanged && onPasswordChanged()
        toast.success('Password changed successfully')
      }
    } catch (err) {
      setSubmitRequest(
        error(err?.response?.data?.detail || 'Something went wrong')
      )
    } finally {
      setSubmitting(false)
    }
  }, [newPassword, onPasswordChanged, oldPassword, validateForm])

  return (
    <Ui.ModalDialog
      opened
      maxWidth={400}
      onClose={onClose}
      title="Change Password"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Ui.PasswordInput
              errorLabel={formErrorLabel?.oldPassword}
              hasError={formError?.oldPassword}
              label="Old Password"
              name="oldPassword"
              onBlur={validateOldPassword}
              onChange={setOldPassword}
              value={oldPassword}
            />
          </div>
          <div className="sm:col-span-4">
            <Ui.PasswordInput
              errorLabel={formErrorLabel?.newPassword}
              hasError={formError?.newPassword}
              label="New Password"
              name="newPassword"
              onBlur={validateNewPassword}
              onChange={setNewPassword}
              value={newPassword}
            />
          </div>
          <div className="mt-4 flex">
            <Ui.PrimaryButton
              disabled={hasFormError || isSubmitting}
              onClick={handleSubmit}
              type="submit"
            >
              {isSubmitting && <Spinner alt="spinner" className="mr-2" />}
              Save
            </Ui.PrimaryButton>
          </div>
          {isError(submitRequest) && (
            <div className="sm:col-span-4">
              <ErrorLabel>{submitRequest.error}</ErrorLabel>
            </div>
          )}
        </div>
      </form>
    </Ui.ModalDialog>
  )
}

export default ModalChangePassword
